import { getCurrencyForClient } from '../currencyHelper'
import { isValidGtmId } from '../gtmId'
import { getEnrollmentPaymentStatus, getTotalPriceForEnrollment } from '../PaymentHelper'
import { Activity, Client, Enrollment, EnrollmentPaymentStatus } from '../../types/GrooverTypes'
import { ensureGtmContainer } from './ensureGtmContainer'
import { oreToMajorUnits, toGtmActivityMetadata } from './gtmActivityMetadata'
import { GtmBookingPayload, GtmRegistrationEvent } from './gtmTypes'

type GtmWindow = typeof globalThis & {
  dataLayer?: Record<string, unknown>[]
}

export const GTM_BOOKING_PAID_BEACON_NONCE_FIELD = '_gtmBookingPaidBeaconNonce'

export const GTM_FIRED_STORAGE_PREFIX = 'gtm:fired:'

export const gtmEventId = (enrollmentId: string, event: GtmRegistrationEvent): string => {
  return event === GtmRegistrationEvent.Booking ? `${enrollmentId}_booking` : `${enrollmentId}_paid`
}

export const hasFiredGtmEvent = (eventId: string): boolean => {
  if (typeof sessionStorage === 'undefined') return false
  try {
    return sessionStorage.getItem(`${GTM_FIRED_STORAGE_PREFIX}${eventId}`) === '1'
  } catch {
    return false
  }
}

export const markGtmEventFired = (eventId: string): void => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(`${GTM_FIRED_STORAGE_PREFIX}${eventId}`, '1')
  } catch {
    // Ignore quota / private-mode failures — in-memory skip still happens via sessionStorage miss.
  }
}

export const resolveGtmBookingValueMajor = (args: {
  enrollment?: Pick<Enrollment, 'priceDetails'> | null
  fallbackValueMajor?: number
}): number => {
  if (args.enrollment?.priceDetails && args.enrollment.priceDetails.length > 0) {
    return oreToMajorUnits(getTotalPriceForEnrollment(args.enrollment))
  }
  return args.fallbackValueMajor ?? 0
}

export const buildGtmBookingPayload = (args: {
  event: GtmRegistrationEvent
  enrollmentId: string
  client: Client
  primaryActivity: Activity
  additionalActivities?: Activity[]
  enrollment?: Pick<Enrollment, 'priceDetails'> | null
  fallbackValueMajor?: number
}): GtmBookingPayload => {
  const activity = toGtmActivityMetadata(args.primaryActivity)
  const extras = (args.additionalActivities ?? []).filter((item) => item.id !== args.primaryActivity.id)
  const payload: GtmBookingPayload = {
    event: args.event,
    eventId: gtmEventId(args.enrollmentId, args.event),
    currency: getCurrencyForClient(args.client),
    value: resolveGtmBookingValueMajor({
      enrollment: args.enrollment,
      fallbackValueMajor: args.fallbackValueMajor
    }),
    activity
  }

  const allActivities = [args.primaryActivity, ...extras]
  if (allActivities.length > 1) {
    payload.activities = allActivities.map(toGtmActivityMetadata)
  }

  return payload
}

const pushGtmEvent = (args: { gtmId: string | undefined; payload: GtmBookingPayload }): boolean => {
  if (!isValidGtmId(args.gtmId)) return false
  if (!ensureGtmContainer(args.gtmId)) return false
  if (hasFiredGtmEvent(args.payload.eventId)) return false

  const gtmWindow = globalThis as GtmWindow
  gtmWindow.dataLayer = gtmWindow.dataLayer || []
  gtmWindow.dataLayer.push({ ...args.payload })
  markGtmEventFired(args.payload.eventId)
  return true
}

export type PushGtmBookingArgs = {
  gtmId: string | undefined
  enrollmentId: string
  client: Client
  primaryActivity: Activity
  additionalActivities?: Activity[]
  enrollment?: Pick<Enrollment, 'priceDetails'> | null
  fallbackValueMajor?: number
}

export const pushBookingEvent = (args: PushGtmBookingArgs): boolean => {
  return pushGtmEvent({
    gtmId: args.gtmId,
    payload: buildGtmBookingPayload({ ...args, event: GtmRegistrationEvent.Booking })
  })
}

export const pushBookingPaidEvent = (args: PushGtmBookingArgs): boolean => {
  return pushGtmEvent({
    gtmId: args.gtmId,
    payload: buildGtmBookingPayload({ ...args, event: GtmRegistrationEvent.BookingPaid })
  })
}

export const shouldPushBookingPaid = (input: {
  isPaid: boolean
  paymentStatus?: EnrollmentPaymentStatus
  isFreeCourseOnStep5: boolean
  arrivedAlreadyPaid: boolean
  bookingFiredInSession: boolean
  isUrlResume: boolean
}): boolean => {
  if (input.paymentStatus === EnrollmentPaymentStatus.PartlyPaid && !input.isPaid) {
    return false
  }

  const isFullyPaid = input.isPaid || input.paymentStatus === EnrollmentPaymentStatus.Paid
  if (isFullyPaid) {
    if (input.arrivedAlreadyPaid && !input.bookingFiredInSession) return false
    return true
  }

  if (input.isFreeCourseOnStep5) {
    if (input.isUrlResume && !input.bookingFiredInSession) return false
    return true
  }

  return false
}

export const shouldFireGtmBookingPaidAfterManualPayment = (args: {
  previousEnrollment?: Enrollment | null
  updatedEnrollment: Enrollment
  client: Client | undefined | null
}): boolean => {
  if (!args.client) {
    console.warn('[gtm] booking_paid skipped: client is empty', {
      enrollmentId: args.updatedEnrollment.id
    })
    return false
  }
  if (getTotalPriceForEnrollment(args.updatedEnrollment) <= 0) {
    return false
  }
  if (args.previousEnrollment && getEnrollmentPaymentStatus(args.previousEnrollment) === EnrollmentPaymentStatus.Paid) {
    return false
  }
  if (getEnrollmentPaymentStatus(args.updatedEnrollment) !== EnrollmentPaymentStatus.Paid) {
    return false
  }

  const primaryActivity = args.updatedEnrollment.activities?.[0]
  if (!primaryActivity) return false
  if (!isValidGtmId(args.client.gtmId)) return false
  return true
}

export const maybePushBookingPaidAfterManualPayment = (args: {
  previousEnrollment: Enrollment
  updatedEnrollment: Enrollment
  client: Client | undefined | null
}): boolean => {
  if (!shouldFireGtmBookingPaidAfterManualPayment(args)) return false

  const activities = args.updatedEnrollment.activities ?? []
  const primaryActivity = activities[0]
  if (!primaryActivity) return false

  return pushBookingPaidEvent({
    gtmId: args.client!.gtmId,
    enrollmentId: args.updatedEnrollment.id,
    client: args.client!,
    primaryActivity,
    additionalActivities: activities,
    enrollment: args.updatedEnrollment
  })
}
